<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div v-if="newKind" class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.new_expense') }} - {{ $t(kindLabel(newKind)) }}
      </oxd-text>
      <oxd-divider />
      <expense-form :kind="newKind" @saved="onSaved" @cancel="newKind = null" />
    </div>
  </div>
  <template v-else>
    <oxd-table-filter :filter-title="$t('expensa.menu_expenses')">
      <oxd-form @submit-valid="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.status"
                type="select"
                :label="$t('general.status')"
                :options="statusOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.kind"
                type="select"
                :label="$t('expensa.col_kind')"
                :options="kindOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.category"
                type="select"
                :label="$t('expensa.col_category')"
                :options="categoryOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="showEmployeeFilter">
              <employee-autocomplete
                v-model="filters.employee"
                :rules="rules.employee"
                :params="{includeEmployees: 'currentAndPast'}"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="filters.fromDate"
                :label="$t('general.from_date')"
                :rules="rules.fromDate"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="filters.toDate"
                :label="$t('general.to_date')"
                :rules="rules.toDate"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.q"
                :label="$t('expensa.filter_text')"
                :rules="rules.q"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.unreported"
                type="checkbox"
                :true-value="true"
                :false-value="false"
                :option-label="$t('expensa.filter_unreported')"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('general.reset')"
            @click="onClickReset"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-button
          :label="$t('expensa.new_expense')"
          icon-name="plus"
          display-type="secondary"
          @click="showKinds = true"
        />
      </div>
      <table-header :total="total" :loading="isLoading" />
      <oxd-text
        v-if="state === 'error'"
        tag="p"
        class="orangehrm-container expensa-empty"
      >
        {{ $t('expensa.list_error') }}
      </oxd-text>
      <oxd-text
        v-else-if="state === 'empty'"
        tag="p"
        class="orangehrm-container expensa-empty"
      >
        {{ $t('expensa.no_expenses') }}
      </oxd-text>
      <div v-else class="orangehrm-container">
        <oxd-card-table
          :items="items.data || []"
          :headers="headers"
          :selectable="false"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>

    <oxd-dialog
      v-if="showKinds"
      class="orangehrm-dialog-modal"
      @update:show="showKinds = false"
    >
      <div class="orangehrm-modal-header">
        <oxd-text type="card-title">{{ $t('expensa.choose_kind') }}</oxd-text>
      </div>
      <oxd-divider />
      <div class="expensa-kinds">
        <oxd-button
          v-for="k in kinds"
          :key="k"
          display-type="secondary"
          :label="$t(kindLabel(k))"
          @click="onChooseKind(k)"
        />
      </div>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="showKinds = false"
        />
      </oxd-form-actions>
    </oxd-dialog>
  </template>
</template>

<script>
import {ref, computed, markRaw} from 'vue';
import {OxdDialog} from '@ohrm/oxd';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import usei18n from '@/core/util/composable/usei18n';
import {
  endDateShouldBeAfterStartDate,
  shouldNotExceedCharLength,
  validDateFormat,
  validSelection,
} from '@/core/util/validation/rules';
import ExpenseForm from '@/orangehrmExpensaPlugin/components/ExpenseForm.vue';
import KindIcon from '@/orangehrmExpensaPlugin/components/KindIcon.vue';
import StatusPill from '@/orangehrmExpensaPlugin/components/StatusPill.vue';
import {expensaApi, pageUrl} from '../util/api';
import {day, eur} from '../util/format';
import {
  EXPENSE_KINDS,
  EXPENSE_STATUSES,
  expenseStatusKey,
  kindKey,
} from '../util/labels';
import {listState} from '../util/listState';

const defaultFilters = {
  status: null,
  kind: null,
  category: null,
  employee: null,
  fromDate: null,
  toDate: null,
  q: '',
  unreported: false,
};

// Esercente, altrimenti percorso o destinazione, altrimenti descrizione.
const subjectOf = (e) => {
  if (e.merchant) return e.merchant;
  if (e.mileage && (e.mileage.routeFrom || e.mileage.routeTo)) {
    return `${e.mileage.routeFrom || '—'} → ${e.mileage.routeTo || '—'}`;
  }
  if (e.perDiem && e.perDiem.destination) return e.perDiem.destination;
  return e.description || '—';
};

/**
 * Importo in una sola cella: "10,00 EUR"; per una valuta diversa dall'euro
 * aggiunge il controvalore, "12,00 USD (≈ 11,05 €)".
 */
const amountOf = (e) => {
  if (!e.amount) return '—';
  const base = `${String(e.amount).replace('.', ',')} ${
    e.currency || ''
  }`.trim();
  if (e.currency && e.currency !== 'EUR' && e.amountEur) {
    return `${base} (≈ ${eur(e.amountEur)})`;
  }
  return base;
};

export default {
  name: 'ExpensaExpenses',
  components: {
    'oxd-dialog': OxdDialog,
    'employee-autocomplete': EmployeeAutocomplete,
    'expense-form': ExpenseForm,
  },
  setup() {
    const {$t} = usei18n();
    const {$tEmpName} = useEmployeeNameTranslate();
    const {userDateFormat} = useDateFormat();
    const filters = ref({...defaultFilters});
    const me = ref(null);

    const serializedFilters = computed(() => ({
      status: filters.value.status?.id ?? null,
      kind: filters.value.kind?.id ?? null,
      categoryId: filters.value.category?.id ?? null,
      empNumber: filters.value.employee?.id ?? null,
      fromDate: filters.value.fromDate || null,
      toDate: filters.value.toDate || null,
      q: filters.value.q ? filters.value.q.trim() || null : null,
      unreported: filters.value.unreported ? true : null,
    }));

    const normalizer = (data) =>
      data.map((e) => ({
        id: e.id,
        kind: e.kind,
        date: day(e.date),
        owner: $tEmpName(e.owner),
        category: e.categoryName || '—',
        subject: subjectOf(e),
        amount: amountOf(e),
        status: $t(expenseStatusKey(e.status)),
        statusTone: e.status,
        report: e.report ? e.report.title : '—',
      }));

    const http = expensaApi('/expenses');
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      normalizer,
      query: serializedFilters,
      toastNoRecords: false,
    });

    return {
      http,
      me,
      userDateFormat,
      filters,
      showPaginator,
      currentPage,
      total,
      pages,
      items: response,
      isLoading,
      execQuery,
    };
  },
  data() {
    return {
      showKinds: false,
      newKind: null,
      kinds: EXPENSE_KINDS,
      categories: [],
      headers: [
        {
          name: 'kindIcon',
          title: '',
          style: {flex: 1},
          cellRenderer: this.iconRenderer,
        },
        {name: 'date', title: this.$t('general.date'), style: {flex: 2}},
        {
          name: 'owner',
          title: this.$t('expensa.col_employee'),
          style: {flex: 3},
        },
        {
          name: 'category',
          title: this.$t('expensa.col_category'),
          style: {flex: 3},
        },
        {
          name: 'subject',
          title: this.$t('expensa.col_subject'),
          cellType: 'oxd-table-cell-truncate',
          style: {flex: 4},
        },
        {name: 'amount', title: this.$t('general.amount'), style: {flex: 3}},
        {
          name: 'status',
          title: this.$t('general.status'),
          style: {flex: 2},
          cellRenderer: this.statusRenderer,
        },
        {
          name: 'report',
          title: this.$t('expensa.col_report'),
          style: {flex: 3},
        },
        {
          name: 'actions',
          slot: 'right',
          title: this.$t('general.actions'),
          style: {flex: 2},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            open: {
              onClick: this.onClickOpen,
              component: 'oxd-button',
              props: {
                label: this.$t('expensa.open'),
                displayType: 'text',
                size: 'medium',
              },
            },
          },
        },
      ],
    };
  },
  computed: {
    rules() {
      return {
        employee: [validSelection],
        q: [shouldNotExceedCharLength(100)],
        fromDate: [validDateFormat(this.userDateFormat)],
        toDate: [
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.filters.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
      };
    },
    state() {
      return listState(this.isLoading, this.items, this.total);
    },
    showEmployeeFilter() {
      return !!this.me && (this.me.isFinance || this.me.isSupervisor);
    },
    statusOptions() {
      return EXPENSE_STATUSES.map((id) => ({
        id,
        label: this.$t(expenseStatusKey(id)),
      }));
    },
    kindOptions() {
      return EXPENSE_KINDS.map((id) => ({id, label: this.$t(kindKey(id))}));
    },
    categoryOptions() {
      return this.categories.map((c) => ({id: c.id, label: c.name}));
    },
  },
  beforeMount() {
    expensaApi('/me')
      .getAll()
      .then(({data}) => {
        this.me = data.data;
      })
      .catch(() => {
        // senza `me` il filtro dipendente resta nascosto: l'elenco funziona comunque
      });
    expensaApi('/categories')
      .getAll({includeInactive: true})
      .then(({data}) => {
        this.categories = data.data;
      })
      .catch(() => {
        // senza categorie il filtro resta vuoto: l'elenco funziona comunque
      });
  },
  methods: {
    kindLabel: kindKey,
    iconRenderer(...[, , , row]) {
      return {component: markRaw(KindIcon), props: {kind: row.kind}};
    },
    statusRenderer(...[, , , row]) {
      return {
        component: markRaw(StatusPill),
        props: {label: row.status, tone: row.statusTone},
      };
    },
    async filterItems() {
      await this.execQuery();
    },
    onClickReset() {
      this.filters = {...defaultFilters};
      this.filterItems();
    },
    onClickOpen(item) {
      window.location.href = pageUrl('viewExpense', item.id);
    },
    onChooseKind(kind) {
      this.showKinds = false;
      this.newKind = kind;
    },
    onSaved(expense) {
      window.location.href = pageUrl('viewExpense', expense.id);
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-empty {
  padding: 0 1.2rem 1.2rem;
}
.expensa-kinds {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
